import { Chip, ComboBox } from 'components';
import { SharedEditableCell } from './SharedEditableCell';
import { useEditableContextOptional } from './OptimizedTableProvider';
import { TableTooltip } from './TableTooltip';
import type { ChipCellProps } from '../types';

/**
 * ChipCell - A cell component that displays a value as a colored Chip
 *
 * Features:
 * - Display value as colored chip
 * - Optional inline editing with dropdown
 * - Configurable colors and borders
 *
 * @example
 * ```tsx
 * // Read-only chip
 * <ChipCell
 *   context={context}
 *   label={row.isActive ? 'Active' : 'Inactive'}
 *   background={row.isActive ? 'green' : 'grey'}
 * />
 *
 * // Editable chip with dropdown
 * <ChipCell
 *   context={context}
 *   label={statusMap[row.status]}
 *   background={getStatusColor(row.status)}
 *   editable
 *   options={statusOptions}
 *   onUpdate={(value) => updateStatus(row.id, value.id)}
 *   editableRowIndex={editableRowIndex}
 *   editableColumnId={editableColumnId}
 * />
 * ```
 */
export const ChipCell = <T extends { id: number | string }>({
  context,
  label,
  background,
  color,
  isBordered = false,
  editable = false,
  options = [],
  onUpdate,
  editableRowIndex: editableRowIndexProp = null,
  editableColumnId: editableColumnIdProp = null,
  isSupplierEditable = false,
  hideActions = false,
  onClearEdit: onClearEditProp,
}: ChipCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const onClearEdit =
    onClearEditProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const chipElement = (
    <TableTooltip title={label}>
      <Chip
        size="sm"
        isBordered={isBordered}
        isBackgroundFilled
        isTooltip={false}
        label={label}
        background={background}
        color={color}
      />
    </TableTooltip>
  );

  // Read-only mode
  if (!editable) {
    return chipElement;
  }

  // Editable mode with dropdown
  return (
    <SharedEditableCell
      {...context}
      hideActions={hideActions}
      meta={{ editable: true }}
      isEditable={context.row.index === editableRowIndex && context.column.id === editableColumnId}
      readModeEl={chipElement}
      isSupplierEditable={isSupplierEditable}
      onClearEdit={onClearEdit}
    >
      <ComboBox
        id={`chip-cell-${context.column.id}`}
        options={options}
        open
        value={label}
        onChange={(e, value) => {
          if (typeof value !== 'string' && !Array.isArray(value) && value?.id && onUpdate) {
            onUpdate(value);
            onClearEdit();
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') onClearEdit?.();
        }}
      />
    </SharedEditableCell>
  );
};
